import React from "react";
import DocsCallout from "../../../components/dashboard/common/Documentation/DocsCallout";
import DocsPageHeader from "../../../components/dashboard/common/Documentation/DocsPageHeader";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";

const Scoping = () => {
  return (
    <SearchHighlighter>
      <DocsPageHeader 
        number="2" 
        title="Target Registration & Scoping" 
        description="Targets define the precise technical boundaries of your testing engagement. After registering an Asset, you must define the individual endpoints, IPs, or mobile packages that researchers are authorized to test."
      />

      <h2 id="asset-mappings" className="text-xl font-bold text-black mt-10 mb-4">2.1 Asset to Target Type Mappings</h2>
      <p className="mb-6 text-slate-600">Selecting the correct target template ensures that researchers receive the right scanning tools and manual testing context. Use the table below to map your asset types to the supported target templates.</p>
      
      <div className="overflow-x-auto border border-[#eaeaea] rounded-xl mb-10">
        <table className="min-w-full divide-y divide-[#eaeaea] m-0">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Primary Asset Type</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Recommended Target Templates</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Web Application</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Web URL</span>
                  <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded text-[10px] font-bold">Admin/Restricted Web URL</span>
                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded text-[10px] font-bold">API Base URI</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">API Service</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded text-[10px] font-bold">API Base URI</span>
                  <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-100 rounded text-[10px] font-bold">Webhook/Callback URL</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Mobile Application</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-100 rounded text-[10px] font-bold">Android App (APK/AAB)</span>
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">iOS App (IPA/TestFlight)</span>
                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded text-[10px] font-bold">Backend API Base URI</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">External/Internal Infrastructure</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">IP Address / Hostname</span>
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">CIDR / Network Segment</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Web Application URL</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Infrastructure (Cloud)</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-100 rounded text-[10px] font-bold">AWS Account / ARN</span>
                  <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-100 rounded text-[10px] font-bold">Azure Subscription / GCP Project ID</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Code & Supply Chain</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Source Code Repository</span>
                  <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded text-[10px] font-bold">CI/CD Pipeline URL</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Integrations & Devices</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded text-[10px] font-bold">External Integration (API)</span>
                  <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-100 rounded text-[10px] font-bold">Webhook / Callback URL</span>
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">Device / IoT IP or Host</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Identity & Data</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Identity Provider Portal</span>
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">Data Storage Endpoint</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="target-fields" className="text-xl font-bold text-black mt-12 mb-4">2.2 Target Field Specification</h2>
      <p className="text-slate-600 mb-6 leading-relaxed">Defining a target requires four primary technical inputs that establish the testing engagement's baseline.</p>
      
      <div className="space-y-6 mb-12">
        <div className="border-l-4 border-blue-500 pl-6 py-1">
          <h4 className="font-bold text-black">Target Name</h4>
          <p className="text-sm text-slate-500 mt-1 italic">Example: "Production Payment API" or "Public Facing WebApp"</p>
          <p className="text-xs text-slate-400 mt-2">A human-readable label used for reporting, filtering, and program assignment. This doesn't affect technical scanning but is critical for audit traceability.</p>
        </div>

        <div className="border-l-4 border-cyan-500 pl-6 py-1">
          <h4 className="font-bold text-black">Technical Template</h4>
          <p className="text-sm text-slate-500 mt-1 italic">Selection: Web URL, API Base, Mobile App (APK/IPA), etc.</p>
          <p className="text-xs text-slate-400 mt-2">Specifies the payload parser and scanning engine. Use the mapping table in Section 2.1 to select the most relevant template for your asset.</p>
        </div>

        <div className="border-l-4 border-slate-300 pl-6 py-1">
          <h4 className="font-bold text-black">Target Value / URI</h4>
          <p className="text-sm text-slate-500 mt-1 italic">Example: <code>https://api.v1.example.com</code> or <code>10.0.5.1/24</code></p>
          <p className="text-xs text-slate-400 mt-2">The core technical identifier. For web targets, this must be the full URI including path if applicable. For infrastructure, enter the IP or CIDR range.</p>
        </div>

        <div className="border-l-4 border-slate-300 pl-6 py-1">
          <h4 className="font-bold text-black">Security Environment</h4>
          <p className="text-sm text-slate-500 mt-1 italic">Production, Staging, UAT, or Development</p>
          <p className="text-xs text-slate-400 mt-2">Specifies the environment's criticality. This affects the **Rules of Engagement** (Section 2.4). Production testing requires higher redundancy and strict throughput limits.</p>
        </div>

        <div className="border-l-4 border-orange-500 pl-6 py-1">
          <h4 className="font-bold text-black">Internet Exposure</h4>
          <p className="text-sm text-slate-500 mt-1 italic">External vs. Internal / Private</p>
          <p className="text-xs text-slate-400 mt-2">
            <b>External:</b> Publicly accessible over the internet via CTB's cloud infrastructure.<br/>
            <b>Internal:</b> Resides in a private network (VPC/LAN) and requires a CTB VPN Gateway or Local Agent for connectivity.
          </p>
        </div>
      </div>

      <h2 id="creation-modes" className="text-xl font-bold text-black mt-12 mb-4">2.3 Target Creation Modes</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
        <div className="rounded-2xl border border-[#eaeaea] p-6 bg-[#fafafa]/50">
          <h3 className="text-lg font-bold text-black mb-3">Table Mode (Standard)</h3>
          <p className="text-sm text-slate-600 mb-4 leading-relaxed">
            Ideal for precise, manual entry of key technical boundaries. Validates inputs in real-time.
          </p>
          <ul className="text-xs space-y-2 list-none p-0 text-slate-500">
            <li className="flex items-center gap-2">
               <span className="w-1 h-1 rounded-full bg-[#0070f3] shrink-0" />
               Validation against RegEx patterns for IP/URL/CIDR.
            </li>
            <li className="flex items-center gap-2">
               <span className="w-1 h-1 rounded-full bg-[#0070f3] shrink-0" />
               Environment-specific configuration (Prod vs Staging).
            </li>
            <li className="flex items-center gap-2">
               <span className="w-1 h-1 rounded-full bg-[#0070f3] shrink-0" />
               Direct binary upload for mobile application targets.
            </li>
          </ul>
        </div>
        <div className="rounded-2xl border border-[#eaeaea] p-6 bg-[#fafafa]/50">
          <h3 className="text-lg font-bold text-black mb-3">Bulk Mode (Import)</h3>
          <p className="text-sm text-slate-600 mb-4 leading-relaxed">
            Scale your onboarding by importing entire target inventories. Supports CSV and JSON formats.
          </p>
          <div className="p-3 bg-white border border-slate-200 rounded-xl mb-4 text-[10px] text-slate-500 font-mono">
            <b>CSV Schema:</b> target_name, target_type, target_value, environment, exposure, notes
          </div>
          <ul className="text-xs space-y-2 list-none p-0 text-slate-500">
            <li className="flex items-center gap-2">
               <span className="w-1 h-1 rounded-full bg-[#0070f3] shrink-0" />
               Validation against case-insensitive environment names.
            </li>
            <li className="flex items-center gap-2">
               <span className="w-1 h-1 rounded-full bg-[#0070f3] shrink-0" />
               Target Type must match the Recommended Templates in Section 2.1.
            </li>
          </ul>
        </div>
      </div>

      <h2 id="safety-handshake" className="text-xl font-bold text-black mt-12 mb-4">2.4 Safety & Compliance Handshake</h2>
      <p className="mb-6">Capture The Bug operates with strict adherence to operational safety. Every target registration requires a dual-confirmation handshake.</p>
      
      <div className="overflow-hidden border border-red-100 rounded-2xl mb-8">
        <div className="bg-red-50 px-6 py-3 border-b border-red-100">
          <span className="text-[10px] font-black text-red-600 uppercase tracking-widest leading-none">Mandatory Checkpoints</span>
        </div>
        <div className="p-6 bg-white space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex-none rounded-lg bg-red-100 flex items-center justify-center text-red-600 font-black text-sm mt-0.5">1</div>
            <div>
              <h4 className="font-bold text-black leading-tight">Scope Authorization</h4>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed italic">"I confirm that my organization is authorized to perform security testing on this target."</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex-none rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 font-black text-sm mt-0.5">2</div>
            <div>
              <h4 className="font-bold text-black leading-tight">Operational Safety</h4>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed italic">"I have verified that this target is part of a non-critical or adequately redundant environment if Production testing is required."</p>
            </div>
          </div>
        </div>
      </div>

      <DocsCallout type="tip">
        <b>Mobile Testing Note:</b> When adding Android or iOS apps, you can either provide store URLs or upload binary files (`.apk`, `.aab`, `.ipa`) directly. Direct uploads are recommended for pre-production builds or restricted apps.
      </DocsCallout>

    </SearchHighlighter>
  );
};

export default Scoping;
